import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  TextField,
  TablePagination,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  Divider,
  ListItemIcon,
  ListItemText,
  Container,
  InputAdornment,
  Snackbar,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { employeeAPI } from '../services/api';
import EmployeeForm from './EmployeeForm';
import ExcelImport from './ExcelImport';
import { Employee, EmployeeSearchFilter } from '../types/Employee';
import * as XLSX from 'xlsx';

const EmployeesList: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<EmployeeSearchFilter>({
    nome: '',
    cognome: '',
    matricola: '',
    codice_fiscale: '',
    dipartimento: '',
    stato: '',
    ente: '',
    regione_sede: '',
    tipo_sede: '',
    ruolo: '',
    page: 1,
    limit: 20,
  });
  const [departments, setDepartments] = useState<string[]>([]);
  const [enti, setEnti] = useState<string[]>([]);
  const [regioni, setRegioni] = useState<string[]>([]);
  const [tipiSede, setTipiSede] = useState<string[]>([]);
  const [ruoli, setRuoli] = useState<string[]>([]);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportDate, setReportDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const [excelMenuAnchor, setExcelMenuAnchor] = useState<null | HTMLElement>(null);
  const [success, setSuccess] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(filter.limit);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMount = useRef(true);

  // Carica i dipartimenti, enti, regioni, tipi sede e ruoli solo al mount
  useEffect(() => {
    loadDepartments();
    loadEnti();
    loadRegioni();
    loadTipiSede();
    loadRuoli();
    // Carica iniziale senza debounce
    loadEmployees();
    isInitialMount.current = false;
  }, []);

  // Debounce per i campi di ricerca testuali (nome, cognome, codice_fiscale, matricola)
  useEffect(() => {
    // Salta l'effetto al primo mount (già caricato sopra)
    if (isInitialMount.current) {
      return;
    }

    // Pulisci il timer precedente se esiste
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Applica debounce di 1500ms (1.5 secondi) per i campi di testo
    // Questo dà più tempo all'utente di finire di digitare prima della ricerca
    debounceTimerRef.current = setTimeout(() => {
      loadEmployees();
    }, 1500);

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.nome, filter.cognome, filter.codice_fiscale, filter.matricola]);

  // Caricamento immediato per dropdown e paginazione
  useEffect(() => {
    // Salta l'effetto al primo mount (già caricato sopra)
    if (isInitialMount.current) {
      return;
    }

    // Per sede, stato, ente, regione, tipo_sede, ruolo, page e limit, carica immediatamente
    loadEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter.dipartimento, filter.stato, filter.ente, filter.regione_sede, filter.tipo_sede, filter.ruolo, filter.page, filter.limit]);

  const loadEmployees = async () => {
    try {
      setLoading(true);
      const data = await employeeAPI.searchEmployees(filter);
      setEmployees(data.employees || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const loadDepartments = async () => {
    try {
      const data = await employeeAPI.getDepartments();
      setDepartments(data.departments || []);
    } catch (err) {
      // Errore('Errore caricamento dipartimenti:', err);
    }
  };

  const loadEnti = async () => {
    try {
      const data = await employeeAPI.getEnti();
      // getEnti() restituisce già un array direttamente
      setEnti(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Errore caricamento enti:', err);
    }
  };

  const loadRegioni = async () => {
    try {
      const data = await employeeAPI.getRegioni();
      setRegioni(data.regioni || []);
    } catch (err) {
      // Errore('Errore caricamento regioni:', err);
    }
  };

  const loadTipiSede = async () => {
    try {
      const data = await employeeAPI.getTipiSede();
      setTipiSede(data.tipi_sede || []);
    } catch (err) {
      // Errore('Errore caricamento tipi sede:', err);
    }
  };

  const loadRuoli = async () => {
    try {
      const data = await employeeAPI.getRuoli();
      setRuoli(data.ruoli || []);
    } catch (err) {
      // Errore('Errore caricamento ruoli:', err);
    }
  };

  const handleFilterChange = (field: keyof EmployeeSearchFilter, value: string | number) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
      page: field !== 'page' ? 1 : (typeof value === 'number' ? value : parseInt(value.toString()) || 1),
    }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setFilter(prev => ({ ...prev, page: newPage + 1 })); // TablePagination usa 0-based index
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setFilter(prev => ({ ...prev, limit: newLimit, page: 1 }));
  };

  const handleCloseSuccess = () => {
    setSuccess('');
  };

  const handleCreateEmployee = () => {
    setSelectedEmployee(null);
    setFormDialogOpen(true);
  };

  const handleEditEmployee = (employee: Employee) => {
    setSelectedEmployee(employee);
    setFormDialogOpen(true);
  };

  const handleDeleteEmployee = async (id: number) => {
    if (window.confirm('Sei sicuro di voler eliminare questo dipendente?')) {
      try {
        await employeeAPI.deleteEmployee(id);
        loadEmployees();
      } catch (err: any) {
        setError(err.response?.data?.error || 'Errore durante l\'eliminazione');
      }
    }
  };

  const handleFormSave = () => {
    loadEmployees();
    setFormDialogOpen(false);
    setSelectedEmployee(null);
    setSuccess('Dipendente salvato con successo!');
  };

  const handleExportToExcel = () => {
    // Prepara i dati per l'export con le stesse colonne del file di import
    const exportData = employees.map(emp => ({
      'Cognome e Nome': `${emp.cognome} ${emp.nome}`,
      'R': '', // Ruolo abbreviato (non presente nel DB)
      'Sede (Misia)': emp.sede || '',
      'prov & cod sede': emp.codice_sede || '',
      'COD FISCALE': emp.codice_fiscale,
      'Tipo Sede': emp.tipo_sede || '',
      'Regione Sede': emp.regione_sede || '',
      'Indirizzo Sede': emp.indirizzo_sede || '',
      'Istituzione Sede': emp.istituzione_sede || '',
      'Pec Sede': emp.pec_sede || '',
      'Email Sede': emp.email_sede || '',
      'Tel Sede': emp.telefono_sede || '',
      'Assunzione': emp.data_assunzione ? new Date(emp.data_assunzione).toLocaleDateString('it-IT') : '',
      'livello': emp.livello || '',
      'Ore sett.': emp.ore_settimanali || 0,
      'Ente': emp.ente || '',
      'T. Det.': '', // Tipo Determinato (non presente nel DB)
      'Fine contr.': '', // Fine Contratto (non presente nel DB)
      'Referente': emp.referente || '',
      'Email Dipendente': emp.email || '',
      'Telefono Dipendente': emp.telefono || '',
      'Riassunzione': emp.riassunzione || '',
      'NOTE': '', // Note (non presente nel DB)
      'Giorni Lavorativi': emp.giorni_settimanali || 0,
      'Lunedì': emp.orario_lunedi || '',
      'Martedì': emp.orario_martedi || '',
      'Mercoledì': emp.orario_mercoledi || '',
      'Giovedì': emp.orario_giovedi || '',
      'Venerdì': emp.orario_venerdi || '',
    }));

    // Crea un workbook e aggiungi un foglio
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dipendenti');

    // Genera il nome file con data e ora
    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const timeStr = now.toTimeString().slice(0, 8).replace(/:/g, '');
    const filename = `dipendenti_export_${dateStr}_${timeStr}.xlsx`;

    // Scarica il file
    XLSX.writeFile(wb, filename);
  };

  const handleExcelMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExcelMenuAnchor(event.currentTarget);
  };

  const handleExcelMenuClose = () => {
    setExcelMenuAnchor(null);
  };

  const handleDownloadTemplate = () => {
    const headers = [
      'Nome',
      'Cognome',
      'Cognome e Nome',
      'Ruolo',
      'Sede (MISIA)',
      'Prov & Cod Sede',
      'Codice Fiscale',
      'Tipo Sede',
      'Regione Sede',
      'Indirizzo Sede',
      'Istituzione Sede',
      'PEC Sede',
      'Email Sede',
      'Tel Sede',
      'Assunzione',
      'Livello',
      'Ore Settimanali',
      'Giorni Lavorativi',
      'Ente',
      'T. Det.',
      'Fine Contr.',
      'Referente',
      'Email Dipendente',
      'Telefono Dipendente',
      'Riassunzione',
      'Note',
      'Gruppo',
      'Lunedì',
      'Martedì',
      'Mercoledì',
      'Giovedì',
      'Venerdì',
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([headers]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template Import');

    const now = new Date();
    const dateStr = now.toISOString().slice(0, 10).replace(/-/g, '');
    const filename = `template_import_dipendenti_${dateStr}.xlsx`;

    XLSX.writeFile(workbook, filename);
    handleExcelMenuClose();
  };

  const downloadCSV = (filename: string, rows: string[][]) => {
    const csv = rows.map(r => r.map(v => '"' + (v ?? '').replace(/"/g, '""') + '"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadLastImportErrors = () => {
    try {
      const raw = localStorage.getItem('lastExcelImportResult');
      if (!raw) { alert('Nessun risultato import disponibile. Esegui un import prima.'); return; }
      const res = JSON.parse(raw);
      const rows: string[][] = [["row", "codice_fiscale", "errors"]];
      (res.errors || []).forEach((e: any) => rows.push([
        String(e.row),
        e.codice_fiscale || '',
        (e.errors || []).join(' | '),
      ]));
      downloadCSV('import-errori.csv', rows);
    } catch {
      alert('Impossibile leggere l\'ultimo report di import.');
    }
  };

  const handleDownloadLastImportCreated = () => {
    try {
      const raw = localStorage.getItem('lastExcelImportResult');
      if (!raw) { alert('Nessun risultato import disponibile. Esegui un import prima.'); return; }
      const res = JSON.parse(raw);
      const rows: string[][] = [["nome", "cognome", "matricola", "codice_fiscale", "username", "password_iniziale"]];
      (res.created_employees || []).forEach((e: any) => rows.push([
        e.nome,
        e.cognome,
        e.matricola,
        e.codice_fiscale,
        e.username || '',
        (e.codice_fiscale?.substring(0,6) || '') + '123',
      ]));
      downloadCSV('import-creati.csv', rows);
    } catch {
      alert('Impossibile leggere l\'ultimo report di import.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'attivo': return 'success';
      case 'inattivo': return 'error';
      case 'in_ferie': return 'warning';
      case 'in_malattia': return 'info';
      default: return 'default';
    }
  };

  const getContractTypeColor = (type: string) => {
    switch (type) {
      case 'tempo_indeterminato': return 'primary';
      case 'tempo_determinato': return 'secondary';
      case 'part_time': return 'info';
      default: return 'default';
    }
  };

  const getPersonIconColor = (status: string) => {
    switch (status) {
      case 'attivo': return '#4caf50'; // green
      case 'inattivo': return '#f44336'; // red
      case 'in_ferie': return '#ff9800'; // orange
      case 'in_malattia': return '#2196f3'; // blue
      default: return '#757575'; // grey
    }
  };

  // Spinner a pagina intera solo al primo caricamento: durante le ricerche
  // successive i campi filtro devono restare montati per non perdere il focus
  if (initialLoading) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4, px: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Gestione Dipendenti
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleCreateEmployee}
            sx={{ mr: 1 }}
          >
            Nuovo
          </Button>
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            endIcon={<ExpandMoreIcon />}
            onClick={handleExcelMenuOpen}
          >
            Excel
          </Button>
          <Menu
            anchorEl={excelMenuAnchor}
            open={Boolean(excelMenuAnchor)}
            onClose={handleExcelMenuClose}
          >
            <MenuItem onClick={() => { setImportDialogOpen(true); handleExcelMenuClose(); }}>
              <UploadIcon sx={{ mr: 1 }} />
              Importa da Excel
            </MenuItem>
            <MenuItem onClick={() => { handleExportToExcel(); handleExcelMenuClose(); }}>
              <DownloadIcon sx={{ mr: 1 }} />
              Esporta in Excel
            </MenuItem>
            <MenuItem onClick={() => { setReportDialogOpen(true); handleExcelMenuClose(); }}>
              <DownloadIcon sx={{ mr: 1 }} />
              Report Presenze (CSV)
            </MenuItem>
            <MenuItem onClick={() => { handleDownloadLastImportCreated(); handleExcelMenuClose(); }}>
              <DownloadIcon sx={{ mr: 1 }} />
              Report Creati (ultimo import)
            </MenuItem>
            <MenuItem onClick={() => { handleDownloadLastImportErrors(); handleExcelMenuClose(); }}>
              <DownloadIcon sx={{ mr: 1 }} />
              Report Errori (ultimo import)
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={handleDownloadTemplate}
              sx={{ 
                backgroundColor: '#e3f2fd',
                '&:hover': { backgroundColor: '#bbdefb' }
              }}
            >
              <ListItemIcon>
                <DownloadIcon color="primary" />
              </ListItemIcon>
              <ListItemText 
                primary="Scarica Template Import"
                secondary="Scarica il file Excel template per l'importazione"
              />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ p: 2, pb: 1 }}>
          <Box display="flex" gap={1} flexWrap="wrap">
            <TextField
              label="Cognome"
              value={filter.cognome}
              onChange={(e) => handleFilterChange('cognome', e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Nome"
              value={filter.nome}
              onChange={(e) => handleFilterChange('nome', e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="CF"
              value={filter.codice_fiscale}
              onChange={(e) => handleFilterChange('codice_fiscale', e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Matricola"
              value={filter.matricola}
              onChange={(e) => handleFilterChange('matricola', e.target.value)}
              size="small"
              sx={{ minWidth: 120 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Sede</InputLabel>
              <Select
                value={filter.dipartimento}
                onChange={(e) => handleFilterChange('dipartimento', e.target.value)}
                label="Sede"
              >
                <MenuItem value="">Tutti</MenuItem>
                {departments.map((dept) => (
                  <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Ente</InputLabel>
              <Select
                value={filter.ente}
                onChange={(e) => handleFilterChange('ente', e.target.value)}
                label="Ente"
              >
                <MenuItem value="">Tutti</MenuItem>
                {enti.map((ente) => (
                  <MenuItem key={ente} value={ente}>{ente}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 100 }}>
              <InputLabel>Stato</InputLabel>
              <Select
                value={filter.stato}
                onChange={(e) => handleFilterChange('stato', e.target.value)}
                label="Stato"
              >
                <MenuItem value="">Tutti</MenuItem>
                <MenuItem value="attivo">Attivo</MenuItem>
                <MenuItem value="inattivo">Inattivo</MenuItem>
                <MenuItem value="in_ferie">In Ferie</MenuItem>
                <MenuItem value="in_malattia">In Malattia</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Regione</InputLabel>
              <Select
                value={filter.regione_sede}
                onChange={(e) => handleFilterChange('regione_sede', e.target.value)}
                label="Regione"
              >
                <MenuItem value="">Tutti</MenuItem>
                {regioni.map((regione) => (
                  <MenuItem key={regione} value={regione}>{regione}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tipo Sede</InputLabel>
              <Select
                value={filter.tipo_sede}
                onChange={(e) => handleFilterChange('tipo_sede', e.target.value)}
                label="Tipo Sede"
              >
                <MenuItem value="">Tutti</MenuItem>
                {tipiSede.map((tipoSede) => (
                  <MenuItem key={tipoSede} value={tipoSede}>{tipoSede}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Tipo Ruolo</InputLabel>
              <Select
                value={filter.ruolo}
                onChange={(e) => handleFilterChange('ruolo', e.target.value)}
                label="Tipo Ruolo"
              >
                <MenuItem value="">Tutti</MenuItem>
                {ruoli.map((ruolo) => (
                  <MenuItem key={ruolo} value={ruolo}>{ruolo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading && <LinearProgress />}

        <TableContainer
          component={Paper}
          sx={{
            maxHeight: { xs: '60vh', lg: '70vh' },
            overflow: 'auto',
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>Cognome</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Ruolo</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Cod. Fiscale</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Tipo Sede</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Regione</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>Indirizzo Sede</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>Istituzione</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>Email Sede</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Tel Sede</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Assunzione</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Livello</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Ore Sett.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Ente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Referente</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 150 }}>Email Dip.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Tel Dip.</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Lunedì</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Martedì</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Mercoledì</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Giovedì</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Venerdì</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 120 }}>Stato</TableCell>
                <TableCell sx={{ fontWeight: 'bold', backgroundColor: 'background.paper', minWidth: 100 }}>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {employees.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={24} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Nessun dipendente trovato
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                employees.map((employee) => (
                  <TableRow 
                    key={employee.id}
                    hover
                  >
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <PersonIcon sx={{ mr: 1, color: getPersonIconColor(employee.stato), fontSize: '1.2rem' }} />
                        <Typography variant="body2" fontWeight="medium">
                          {employee.cognome}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{employee.nome}</TableCell>
                    <TableCell>{employee.ruolo || '-'}</TableCell>
                    <TableCell>{employee.codice_fiscale}</TableCell>
                    <TableCell>{employee.tipo_sede || '-'}</TableCell>
                    <TableCell>{employee.regione_sede || '-'}</TableCell>
                    <TableCell>{employee.indirizzo_sede || '-'}</TableCell>
                    <TableCell>{employee.istituzione_sede || '-'}</TableCell>
                    <TableCell>{employee.email_sede || '-'}</TableCell>
                    <TableCell>{employee.telefono_sede || '-'}</TableCell>
                    <TableCell>{employee.data_assunzione ? new Date(employee.data_assunzione).toLocaleDateString('it-IT') : '-'}</TableCell>
                    <TableCell>{employee.livello || '-'}</TableCell>
                    <TableCell>{employee.ore_settimanali}h</TableCell>
                    <TableCell>{employee.ente || '-'}</TableCell>
                    <TableCell>{employee.referente || '-'}</TableCell>
                    <TableCell>{employee.email || '-'}</TableCell>
                    <TableCell>{employee.telefono || '-'}</TableCell>
                    <TableCell>{employee.orario_lunedi || '-'}</TableCell>
                    <TableCell>{employee.orario_martedi || '-'}</TableCell>
                    <TableCell>{employee.orario_mercoledi || '-'}</TableCell>
                    <TableCell>{employee.orario_giovedi || '-'}</TableCell>
                    <TableCell>{employee.orario_venerdi || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={employee.stato}
                        color={getStatusColor(employee.stato) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton 
                        size="small" 
                        color="primary"
                        onClick={() => handleEditEmployee(employee)}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteEmployee(employee.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={filter.page - 1} // TablePagination usa 0-based index
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} di ${count}`}
        />
      </Card>

      {/* Dialog per import Excel */}
      <Dialog open={importDialogOpen} onClose={() => setImportDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Importa Dipendenti da Excel</DialogTitle>
        <DialogContent>
          <ExcelImport />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Report */}
      <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Scarica Report Giornaliero</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} alignItems="center" mt={1}>
            <TextField
              label="Data"
              type="date"
              value={reportDate}
              onChange={(e) => setReportDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <Button
              variant="contained"
              onClick={() => {
                const base = (typeof window !== 'undefined' ? `${window.location.origin}/api` : '/api');
                window.location.href = `${base}/admin/reports/daily.csv?date=${encodeURIComponent(reportDate)}`;
              }}
            >
              Scarica CSV
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReportDialogOpen(false)}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      {/* Form Dipendente */}
      <EmployeeForm
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        employee={selectedEmployee}
        onSave={handleFormSave}
      />

      {/* Snackbar per messaggio di successo */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EmployeesList;
